import { useState } from 'react';
import { useJobHistory } from './hooks/useJobHistory';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Briefcase } from 'lucide-react';
import { isDuplicateJob } from '../../lib/utils';
import { truncateText } from '../../lib/utils';

interface JobSelectorProps {
  onSelect: (job: { title: string; description: string }) => void;
}

interface GroupedJob {
  id: string;
  title: string;
  jd_text: string;
  created_at: string;
  duplicateCount: number;
  lastSubmitted: string;
}

export function JobSelector({ onSelect }: JobSelectorProps) {
  const { jobs, isLoading } = useJobHistory();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const groupedJobs = jobs.reduce<GroupedJob[]>((acc, current) => {
    const duplicateIndex = acc.findIndex(group => isDuplicateJob(current.title, current.jd_text, [group]));
    if (duplicateIndex >= 0) {
      acc[duplicateIndex].duplicateCount += 1;
      acc[duplicateIndex].lastSubmitted = new Date(Math.max(new Date(acc[duplicateIndex].lastSubmitted).getTime(), new Date(current.created_at).getTime())).toISOString();
    } else {
      acc.push({ ...current, duplicateCount: 1, lastSubmitted: current.created_at });
    }
    return acc;
  }, []);

  if (isLoading) {
    return <Button variant="outline" size="sm" disabled>Loading History...</Button>;
  }

  if (!jobs.length) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="justify-between w-[200px] text-muted-foreground hover:text-foreground"
        >
          <Briefcase className="mr-2 h-4 w-4 flex-shrink-0 text-primary" />
          Select Previous...
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0">
        <Command>
          <CommandInput 
            placeholder="Search job title..." 
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>No matching job found.</CommandEmpty>
            <CommandGroup heading="Previous Jobs">
              <ScrollArea className="h-[200px]">
                {groupedJobs
                  .filter(job => job.title.toLowerCase().includes(searchValue.toLowerCase()))
                  .map((job) => (
                    <CommandItem
                      key={job.id}
                      value={job.title} 
                      onSelect={() => {
                        onSelect({ title: job.title, description: job.jd_text });
                        setOpen(false);
                        setSearchValue("");
                      }}
                      className="cursor-pointer flex flex-col items-start px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
                    >
                      <div className="w-full flex justify-between items-center mb-1">
                        <span className="font-medium text-foreground truncate">{job.title}</span>
                        {job.duplicateCount > 1 && (
                          <Badge variant="secondary" className="ml-2 flex-shrink-0 bg-amber-100 text-amber-700 text-xs px-1.5 py-0">
                            {job.duplicateCount}x
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {truncateText(job.jd_text, 60)}
                      </p>
                    </CommandItem>
                  ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}